import { Add, Close, Save, Settings } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { App, Plugin, Route, View } from "@pimo/pimo-app-builder";
import {
  ChipCell,
  DateCell,
  DerivePMTableProps,
  DragAndDropLayout,
  DragDropLayoutTabDefinition,
  generateTableHeaderEntriesWithSorting,
  PMButton,
  PMPopUp,
  PMSmallNumberCardEditSchema,
  PMTableEventNames,
  PMTableEventPayload,
  PMTableSortEventPayload,
  ProgressBarCell,
  SortDirection,
  StatusIndicatorCell,
  TableUpdateStatusCell,
  TextCardCell,
} from "@pimo/pimo-components";
import PMTable from "@pimo/pimo-components/src/lib/pm-components/pm-table/pm-table";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import dayjs from "dayjs";
import { generatePath } from "react-router-dom";
import {
  APP_CRITICALITIES,
  APP_RECORD_TYPES,
  BasicItem,
  COLORS,
  CONTRAST_TEXT_COLORS,
  Project,
  PROJECT_NAMES,
  ReportingItem,
} from "vmd-types";
import { canUserPerformOperation, getContrastColor } from "vmd-utils";

import { TestAppState } from "../app";
import { BadgeCell } from "../components/badge-cell";
import { DcRelevanceCell } from "../components/dc-relevance-cell";
import { DynamicTablePagination } from "../components/dynamic-table/dynamic-table-component";
import { OpenAssessmentCell } from "../components/open-assessment-cell";
import {
  type FilterData,
  SearchAndFilter,
} from "../components/project-filter-dialog";
import { ReportTableActionsMenu } from "../components/report-table-actions-menu";
import {
  APP_ROUTES,
  CRITICALITY_COLORS_BY_KEY,
  CRITICALITY_TEXT_COLOR,
  getReviewStatusBadgeProps,
  RECORD_TYPE_BADGE_COLORS,
  SECONDARY_TEXT_COLOR,
} from "../constants";
import { buildCreateProjectFormRoute } from "../form/build-create-project-form-route";
import { createDynamicTableState } from "../helpers/dynamic-table";
import { downloadBusinessAppExport } from "../helpers/fetch-business-app-export";
import { fetchOverlay } from "../helpers/fetch-overlay";
import { fetchOverview } from "../helpers/fetch-overview";
import { deleteProject } from "../helpers/fetch-project";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import {
  applyColumnOrderToHeaders,
  doesProjectPassFilterCriteria,
  OVERVIEW_TABLE_FIELD_ORDER,
} from "../helpers/pages-helpers/overview-helpers";
import { applyColumnOrderToHeadersForReportPlugin } from "../helpers/pages-helpers/report-plugin-helpers";
import {
  beginTableConfigurationEdit,
  keepTableConfigurationInEditMode,
  markTableConfigurationDirty,
  markTableConfigurationDirtyFromColumnPayload,
  restorePersistedTableConfiguration,
  saveTableConfigurationAndExitEditMode,
  setTableConfigurationConfirmationPopup,
} from "../helpers/table-configuration-state";
import { dropStaleLayoutComponents } from "../utils/drop-stale-layout-components";
import { isRecordTypeVisible } from "../utils/filter-reporting-items-by-record-type";
import { formatDate, formatDateTime } from "../utils/format-date";
import { getItemCreationDisplayName } from "../utils/get-item-creation-display-name";
import { getLockedByMessage } from "../utils/get-locked-by-message";
import { getOeOwnerLabel } from "../utils/get-owner-label";
import { getRecordTypeBadgeConfig } from "../utils/get-record-type-badge-config";
import { getStatusBadgeColumnWidth } from "../utils/get-status-badge-column-width";
import {
  resolveStatusOrder,
  sortProjects,
  sortReportingItems,
} from "../utils/sort-table-data";
import { OVERVIEW_CARDS } from "./overview";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OverviewPluginState {}

const STALE_OVERVIEW_COMPONENT_IDS = new Set<string>(
  isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)
    ? []
    : ["numberOfITServicesDashboardComponent"]
);

const REPORT_TABLE_FIELD_LOCALE_KEYS = {
  customId: "id",
  name: "system",
  recordType: "systemType",
  owner: "owner",
  distributionChannel: "distributionChannel",
  lifeCycle: "lifeCycleStatus",
  criticality: "criticality",
  processSteps: "processSteps",
  applicationCapabilities: "applicationCapabilities",
  technicalExposure: "technicalExposure",
  degreeOfCompletion: "degreeOfCompletion",
  open: "open",
} as const;

const IT_SYSTEMS_OVERVIEW_TABLE_FIELD_ORDER = [
  "customId",
  "name",
  "recordType",
  "owner",
  "distributionChannel",
  "lifeCycle",
  "criticality",
  "processSteps",
  "applicationCapabilities",
  "technicalExposure",
  "dcRelevantOe",
  "degreeOfCompletion",
  "open",
] as const;

const IT_SYSTEMS_OPEN_COLUMN_WIDTH_PX = 90;

// The DC Systems Overview table's owner column is always labeled "CISO",
// regardless of whether it's accessed via the OE view (previously "ISO")
// or the prod/admin view (previously "Owner").
const IT_SYSTEMS_OVERVIEW_OWNER_LABEL = "CISO";

const getSystemTypeHeaderLabel = (language: string | undefined) =>
  getLocalizedMessage(language || "en", "systemType") || "System Type";

export const getDcRelevantOeDisplay = (
  reportingItem: ReportingItem
): "Yes" | "No" | "Not selected" => reportingItem.dcRelevance ?? "Not selected";

const doesItSystemsOverviewItemPassFilterCriteria = (
  item: ReportingItem,
  filterData: FilterData | undefined,
  settings: TestAppState["settings"] | undefined
): boolean => {
  if (!filterData) {
    return true;
  }

  const selectedSystemTypes = (filterData.systemTypeFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedOwners = (filterData.ownerFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedDistributionChannels = (
    filterData.distributionChannelFilter ?? []
  )
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedLifeCycles = (filterData.lifeCycleFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedCriticalities = (filterData.criticalityFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedReviewStatuses = (filterData.reviewStatusFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  const recordTypeValue = (item.basicItem?.recordType ?? "")
    .trim()
    .toLowerCase();
  if (
    selectedSystemTypes.length > 0 &&
    !selectedSystemTypes.includes(recordTypeValue)
  ) {
    return false;
  }

  const ownerValue = (item.basicItem?.owner ?? "").trim().toLowerCase();
  if (selectedOwners.length > 0 && !selectedOwners.includes(ownerValue)) {
    return false;
  }

  const distributionChannelLabels = (item.distributionChannel ?? []).map(
    (channelId) =>
      (
        settings?.configuration?.distributionChannelDefinition?.find(
          (channel) => channel.id === channelId
        )?.label ?? channelId
      )
        .trim()
        .toLowerCase()
  );
  if (
    selectedDistributionChannels.length > 0 &&
    !distributionChannelLabels.some((label) =>
      selectedDistributionChannels.includes(label)
    )
  ) {
    return false;
  }

  const lifeCycleValue = (item.basicItem?.lifeCycle ?? "").trim().toLowerCase();
  if (
    selectedLifeCycles.length > 0 &&
    !selectedLifeCycles.includes(lifeCycleValue)
  ) {
    return false;
  }

  const criticalityValue = (item.basicItem?.criticality ?? "")
    .trim()
    .toLowerCase();
  if (
    selectedCriticalities.length > 0 &&
    !selectedCriticalities.includes(criticalityValue)
  ) {
    return false;
  }

  const reviewStatusValue = (item.degreeOfCompletion ?? "")
    .trim()
    .toLowerCase();
  if (
    selectedReviewStatuses.length > 0 &&
    !selectedReviewStatuses.includes(reviewStatusValue)
  ) {
    return false;
  }

  const searchQuery = (filterData.searchQueryFilter ?? []).join(" ").trim();
  const normalizedQuery = searchQuery.toLowerCase();

  if (normalizedQuery.length > 0) {
    const name = (item.basicItem?.name ?? "").toLowerCase();
    const customId = (item.basicItem?.customId?.toString() ?? "").toLowerCase();
    const recordType = (item.basicItem?.recordType ?? "").toLowerCase();
    const owner = (item.basicItem?.owner ?? "").toLowerCase();
    const criticality = (item.basicItem?.criticality ?? "").toLowerCase();
    const processSteps = (item.basicItem?.processSteps ?? [])
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const applicationCapabilities = (
      item.basicItem?.applicationCapabilities ?? []
    )
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const technicalExposure = (item.basicItem?.technicalExposure ?? [])
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesQuery =
      name.includes(normalizedQuery) ||
      customId.includes(normalizedQuery) ||
      recordType.includes(normalizedQuery) ||
      owner.includes(normalizedQuery) ||
      criticality.includes(normalizedQuery) ||
      processSteps.includes(normalizedQuery) ||
      applicationCapabilities.includes(normalizedQuery) ||
      technicalExposure.includes(normalizedQuery);

    if (!matchesQuery) {
      return false;
    }
  }

  return true;
};

/**
 * Labels used purely for the export filename (#2510), one per active filter
 * on the "DC Business Apps Overview" table's `FilterData`.
 */
const BUSINESS_APP_EXPORT_FILTER_LABELS: Record<
  | "systemTypeFilter"
  | "ownerFilter"
  | "distributionChannelFilter"
  | "lifeCycleFilter"
  | "criticalityFilter"
  | "reviewStatusFilter"
  | "searchQueryFilter",
  string
> = {
  systemTypeFilter: "System Type",
  ownerFilter: "Owner",
  distributionChannelFilter: "Distribution Channel",
  lifeCycleFilter: "Life Cycle",
  criticalityFilter: "Criticality",
  reviewStatusFilter: "Review Status",
  searchQueryFilter: "Search",
};

export const getActiveBusinessAppFilterLabels = (
  filterData: FilterData | undefined
): string[] => {
  if (!filterData) {
    return [];
  }

  return (
    Object.keys(BUSINESS_APP_EXPORT_FILTER_LABELS) as Array<
      keyof typeof BUSINESS_APP_EXPORT_FILTER_LABELS
    >
  )
    .filter((key) => (filterData[key] ?? []).length > 0)
    .map((key) => BUSINESS_APP_EXPORT_FILTER_LABELS[key]);
};

/**
 * The ids (in the given, currently sorted/displayed order) of the Business
 * Application records that pass the currently active filters, ignoring
 * pagination (#2510). Used to drive the "Download Business Apps" export.
 */
export const getBusinessAppExportItemIds = (
  items: ReportingItem[],
  filterData: FilterData | undefined,
  settings: TestAppState["settings"] | undefined
): string[] =>
  items
    .filter((item) => !item.isChannelHeader)
    .filter(
      (item) =>
        item.basicItem?.recordType === APP_RECORD_TYPES.BUSINESS_APPLICATION
    )
    .filter((item) =>
      doesItSystemsOverviewItemPassFilterCriteria(item, filterData, settings)
    )
    .map((item) => item.id);

export class OverviewPlugin implements Plugin<TestAppState, TestAppState> {
  route?: Route;
  private view?: View<TestAppState, DragAndDropLayout>;
  private app?: App<TestAppState>;
  // private downloadMenuAnchor?: HTMLElement | null;
  onRegister(app: App<TestAppState>): void {
    this.app = app;

    this.view = app.createView({
      name: "Overview",
      layout: new DragAndDropLayout(),
    });

    const view = this.view;
    this.route = app.createRoute({
      path: APP_ROUTES.overview,
      view: view,
    });

    this.view.mapLayoutState(
      ({
        overviewLayout,
        settings,
        userProfile,
        showDeleteProjectDialog,
        pendingDeleteProjectId,
        language,
        overviewTableConfiguration,
      }) => {
        const confirmationPopupType =
          overviewTableConfiguration?.confirmationPopupType;
        const projectOverviewTitle =
          getLocalizedMessage(
            language || "en",
            "overviewPlugin.projectOverviewTabTitle"
          ) || "Project Overview";
        const itSystemsOverviewTitle =
          getLocalizedMessage(
            language || "en",
            "overviewPlugin.itSystemsOverviewTabTitle"
          ) || "DC Business Apps";
        const itSystemsOverviewTitleComponentId =
          "itSystemsOverviewTitleComponent";
        const itSystemsOverviewTableComponentId =
          "itSystemsOverviewTableComponent";

        const ensureItSystemsOverviewRows = (
          rows: {
            id: number;
            columnCount: number;
            columns: { columnIndex: number; componentId: string }[];
          }[] = []
        ) => {
          const hasTitleRow = rows.some((row) =>
            row.columns.some(
              (column) =>
                column.componentId === itSystemsOverviewTitleComponentId
            )
          );

          const hasTableRow = rows.some((row) =>
            row.columns.some(
              (column) =>
                column.componentId === itSystemsOverviewTableComponentId
            )
          );

          const normalizedRows = [...rows];

          if (!hasTitleRow) {
            normalizedRows.unshift({
              id: -1,
              columnCount: 1,
              columns: [
                {
                  columnIndex: 0,
                  componentId: itSystemsOverviewTitleComponentId,
                },
              ],
            });
          }

          if (!hasTableRow) {
            normalizedRows.push({
              id: normalizedRows.length,
              columnCount: 1,
              columns: [
                {
                  columnIndex: 0,
                  componentId: itSystemsOverviewTableComponentId,
                },
              ],
            });
          }

          return normalizedRows.map((row, index) => ({
            ...row,
            id: row.id < 0 ? index : row.id,
          }));
        };

        let tabs;
        if (overviewLayout?.tabs && overviewLayout.tabs.length > 0) {
          const normalizedTabs =
            overviewLayout.tabs.length >= 2
              ? overviewLayout.tabs.map((tab, index) =>
                  index === 1
                    ? {
                        ...tab,
                        rows: ensureItSystemsOverviewRows(tab.rows),
                      }
                    : tab
                )
              : [
                  ...overviewLayout.tabs,
                  {
                    id: 1,
                    title: itSystemsOverviewTitle,
                    rows: ensureItSystemsOverviewRows(),
                  },
                ];

          tabs = normalizedTabs.map((tab, index) => ({
            ...tab,
            title:
              index === 0
                ? projectOverviewTitle
                : index === 1
                  ? itSystemsOverviewTitle
                  : tab.title,
          }));
        } else {
          tabs = [
            {
              id: 0,
              title: projectOverviewTitle,
              rows: [{ id: 0, columnCount: 3, columns: [] }],
            },
            {
              id: 1,
              title: itSystemsOverviewTitle,
              rows: ensureItSystemsOverviewRows(),
            },
          ];
        }
        tabs = dropStaleLayoutComponents(tabs, STALE_OVERVIEW_COMPONENT_IDS);
        return {
          canEdit: canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          }),
          isComponentEditingEnabled: true,

          displayTabs: true,
          language: language || "en",
          tabs,
          rightSlot: (
            <>
              {/* Hide download button until a proper download template is implemented */}
              {/* <Box */}
              {/*   className="no-print" */}
              {/*   sx={{ display: "flex", alignItems: "center", gap: 1 }} */}
              {/* > */}
              {/*   <IconButton */}
              {/*     onClick={(event) => { */}
              {/*       this.downloadMenuAnchor = event.currentTarget; */}
              {/*       this.app?.patchAppState({}); */}
              {/*     }} */}
              {/*     sx={{ ml: 1 }} */}
              {/*   > */}
              {/*     <FileDownloadOutlined /> */}
              {/*   </IconButton> */}
              {/* </Box> */}
              {/* <Menu */}
              {/*   anchorEl={this.downloadMenuAnchor} */}
              {/*   open={Boolean(this.downloadMenuAnchor)} */}
              {/*   onClose={() => { */}
              {/*     this.downloadMenuAnchor = null; */}
              {/*     this.app?.patchAppState({}); */}
              {/*   }} */}
              {/* > */}
              {/*   <MenuItem */}
              {/*     onClick={() => { */}
              {/*       setTimeout(() => { */}
              {/*         window.print(); */}
              {/*       }, 1000); */}
              {/*       this.downloadMenuAnchor = null; */}
              {/*       this.app?.patchAppState({}); */}
              {/*     }} */}
              {/*   > */}
              {/*     Download PDF */}
              {/*   </MenuItem> */}
              {/* </Menu> */}
              <PMPopUp
                open={!!showDeleteProjectDialog}
                title={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.title.confirmDelete"
                  ) || "Confirm Delete"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.message.genericConfirm",
                    { itemName: settings?.configuration?.projectName ?? "" }
                  ) ||
                  `Are you sure you want to delete this ${settings?.configuration?.projectName}?`
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showDeleteProjectDialog: false,
                    pendingDeleteProjectId: undefined,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showDeleteProjectDialog: false,
                    pendingDeleteProjectId: undefined,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="error"
                confirmationButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.delete"
                  ) || "Delete"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;
                  const projectId = pendingDeleteProjectId;
                  if (projectId) {
                    try {
                      await deleteProject(projectId);
                      this.app.patchAppState({
                        overlay: await fetchOverlay(),
                        overview: await fetchOverview(),
                      });
                    } catch {
                      // ignore errors for now
                    }
                  }

                  this.app.patchAppState({
                    showDeleteProjectDialog: false,
                    pendingDeleteProjectId: undefined,
                  });
                }}
              />
              {/* Confirmation dialog for saving overview layout changes across overview pages */}
              <PMPopUp
                open={
                  !!(
                    this.app &&
                    this.app.getAppState().showConfirmSaveOverviewLayoutDialog
                  )
                }
                title={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.title"
                  ) || "Save layout changes?"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.message"
                  ) || "Do you want to save the layout changes?"
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showConfirmSaveOverviewLayoutDialog: false,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showConfirmSaveOverviewLayoutDialog: false,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  getLocalizedMessage(language || "en", "dialog.button.save") ||
                  "Save"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;

                  const { pendingSaveOverviewLayout } = this.app.getAppState();

                  // clear dialog flag
                  this.app.patchAppState({
                    showConfirmSaveOverviewLayoutDialog: false,
                  });

                  if (pendingSaveOverviewLayout) {
                    try {
                      const { overviewLayout } = this.app.getAppState();
                      await createViewConfiguration({
                        tabs: pendingSaveOverviewLayout.tabs,
                        canEdit: overviewLayout?.canEdit ?? true,
                        displayTabs: pendingSaveOverviewLayout.displayTabs,
                        type: pendingSaveOverviewLayout.type ?? "overview",
                      });
                      // success snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            getLocalizedMessage(
                              language || "en",
                              "layoutSavedSuccess"
                            ) ||
                            "Layout saved. Changes have been applied to all overview pages.",
                          severity: "success",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    } catch {
                      // error snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            "Failed to save overview layout changes. Please try again.",
                          severity: "error",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    }
                  } else {
                    // if not confirmed, revert the in-memory layout to the last saved layout
                    const { overviewLayout } = this.app.getAppState();
                    if (overviewLayout && overviewLayout.tabs) {
                      this.app.patchAppState({
                        overviewLayout: overviewLayout,
                      });
                    }
                  }

                  // remove pending data
                  this.app.patchAppState({
                    pendingSaveOverviewLayout: undefined,
                  });
                }}
              />
              <PMPopUp
                open={!!confirmationPopupType}
                title={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.title.unsavedChanges"
                  ) || "Unsaved Changes"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.message.abortTableConfiguration"
                  ) ||
                  "You have made changes that have not been saved. If you close the table configurator, these changes will be lost."
                }
                onClose={async () => {
                  if (!this.app) {
                    return;
                  }

                  keepTableConfigurationInEditMode(
                    this.app,
                    "overviewTableConfiguration"
                  );
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={async () => {
                  if (!this.app) {
                    return;
                  }

                  keepTableConfigurationInEditMode(
                    this.app,
                    "overviewTableConfiguration"
                  );
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.button.save"
                      ) || "Save"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.button.continue"
                      ) || "Continue"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;
                  await restorePersistedTableConfiguration({
                    app: this.app,
                    configKey: "overviewTableConfiguration",
                    columnOrderKey: "overviewPluginColumnOrder",
                    componentId: "projectsTableComponent",
                  });
                }}
              />
            </>
          ),
        };
      }
    );
    buildCreateProjectFormRoute(app, this.route, true);
    this.view.on("updateLayout", async ({ payload }) => {
      if (!payload || !this.app) return;

      const currentDisplayTabs = true;

      const { tabs } = payload as { tabs: DragDropLayoutTabDefinition[] };

      // apply in-memory so UI updates immediately
      this.app.patchAppState({
        overviewLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      // open confirmation dialog and store pending layout — defer persisting until confirmed
      this.app.patchAppState({
        pendingSaveOverviewLayout: {
          tabs,
          displayTabs: currentDisplayTabs,
          type: "overview",
        },
        showConfirmSaveOverviewLayoutDialog: true,
      });
    });
    this.view?.on("startLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: false,
      });
    });

    this.view?.on("stopLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: true,
      });
    });

    this.view.buildDynamicallyBasedOnAppState(
      (appState) => ({
        configuration: appState.settings?.configuration,
        project: appState.currentReport?.project,
      }),

      (state, view) => {
        // static components are built here
        this.buildOverview();

        const enabledProjectCustomFieldsForTable =
          state.configuration?.customFields?.filter(
            (field) =>
              field?.enabled &&
              field?.flag !== "delete" &&
              (field?.purpose === "projectBasic" ||
                field?.purpose === "projectReporting")
          ) ?? [];

        const baseProjectsTableDefinition = [
          { component: TextCardCell },
          { component: TextCardCell },
          { component: TextCardCell },
          { component: ChipCell },
          { component: ChipCell },
          { component: ChipCell },
          { component: ChipCell },
          { component: ChipCell },
          { component: TableUpdateStatusCell },
        ];

        const chipProps = {
          sx: {
            backgroundColor: RECORD_TYPE_BADGE_COLORS.businessApplication,
            px: 2,
            borderRadius: "8px",
            fontWeight: 500,
          },
        };

        const projectsTableDefinition: Array<{
          component:
            | typeof TextCardCell
            | typeof ChipCell
            | typeof BadgeCell
            | typeof StatusIndicatorCell
            | typeof ProgressBarCell
            | typeof DateCell
            | typeof TableUpdateStatusCell;
        }> = [
          ...baseProjectsTableDefinition,
          ...enabledProjectCustomFieldsForTable.map((field) => ({
            component:
              field.type === "Date"
                ? DateCell
                : field.type === "Select"
                  ? BadgeCell
                  : TextCardCell,
          })),
        ];

        const projectsTable = new PMTable<typeof projectsTableDefinition>(
          projectsTableDefinition
        );

        const projectsTableComponent = view.addComponent<
          DerivePMTableProps<typeof projectsTable>,
          PMTableEventNames,
          PMTableEventPayload
        >({
          component: projectsTable,
          layoutProps: {
            componentId: "projectsTableComponent",
            labels: ["#ofProjects"],
            description: "",
            title: "",
            editSchema: PMSmallNumberCardEditSchema,
          },
        });

        projectsTableComponent.mapLayoutProps(({ language }) => ({
          componentId: "projectsTableComponent",
          labels: ["#ofProjects"],
          description:
            getLocalizedMessage(
              language || "en",
              "overviewPlugin.projectsTableDescription"
            ) || `Shows the number of Projects.`,
          title:
            getLocalizedMessage(
              language || "en",
              "overviewPlugin.projectsTableTitle"
            ) || `Projects`,
          editSchema: PMSmallNumberCardEditSchema,
        }));

        projectsTableComponent.on("sort", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          const payload = event?.payload as PMTableSortEventPayload<
            keyof Project
          >;
          const state = this.app.getAppState();
          let direction: SortDirection = "ASC";

          // Swap direction if key is already being sorted by in ascending order.
          if (
            state.overviewPluginSortData?.key === payload?.sort?.key &&
            state.overviewPluginSortData?.direction === "ASC"
          ) {
            direction = "DESC";
          }

          this.app.patchAppState({
            overviewPluginSortData: {
              key: payload?.sort?.key,
              direction,
            },
          });
        });

        projectsTableComponent.mapState(
          ({
            overview,
            settings,
            overviewPluginSortData,
            overviewPluginColumnOrder,
            overviewTablePagination,
            userProfile,
            language,
            isSidebarOpen,
            filterData,
            overviewTableConfiguration,
            lockDetails,
          }) => {
            const resourceLockPlugin =
              this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
                "ResourceLockPlugin"
              );

            const lockedProjects = lockDetails?.projectLocks;
            const projectItems = sortProjects(
              overview?.projects ?? [],
              overviewPluginSortData
            );
            const isOwnerEnabled =
              settings?.configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "projectBasic")
                ?.find((field) => field.fieldName === "owner")?.enabled ??
              false;

            const oeFilterOptions = [
              ...new Set(
                projectItems
                  .map((project) => project?.name?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const ownerFilterOptions = [
              ...new Set(
                projectItems
                  .map((project) => project?.owner?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const updateStatusFilterOptions = [
              ...new Set(
                projectItems
                  .map((project) => project?._calculated?.updateStatus?.status)
                  .map((status) => status?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const rawTableHeaderEntries =
              overviewTableConfiguration?.tableHeaderEntries ?? [];

            const toCustomColumnFieldName = (label?: string) => {
              const normalizedLabel = (label ?? "")
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "");

              return `customField-${normalizedLabel || "unnamed"}`;
            };

            const enabledProjectCustomFields =
              settings?.configuration?.customFields?.filter(
                (field) =>
                  field?.enabled &&
                  field?.flag !== "delete" &&
                  (field?.purpose === "projectBasic" ||
                    field?.purpose === "projectReporting")
              ) ?? [];

            const customLabelByFieldName = new Map(
              enabledProjectCustomFields.map((field) => [
                toCustomColumnFieldName(field.label),
                field.label,
              ])
            );

            const customFieldDefinitionByFieldName = new Map(
              enabledProjectCustomFields.map((field) => [
                toCustomColumnFieldName(field.label),
                field,
              ])
            );

            const enabledCustomFieldNames = new Set(
              customLabelByFieldName.keys()
            );

            const orderedUniqueHeaders = rawTableHeaderEntries.filter(
              (entry, index, source) =>
                source.findIndex((e) => e.fieldName === entry.fieldName) ===
                index
            );

            const orderedUniqueKnownHeaders = orderedUniqueHeaders.filter(
              (entry) =>
                OVERVIEW_TABLE_FIELD_ORDER.includes(
                  entry.fieldName as (typeof OVERVIEW_TABLE_FIELD_ORDER)[number]
                )
            );

            const orderedUniqueCustomHeaders = orderedUniqueHeaders
              .filter(
                (entry) =>
                  String(entry.fieldName).startsWith("customField-") &&
                  enabledCustomFieldNames.has(String(entry.fieldName))
              )
              .map((entry) => ({
                ...entry,
                label:
                  customLabelByFieldName.get(String(entry.fieldName)) ??
                  entry.label,
              }));

            const existingCustomFieldNames = new Set(
              orderedUniqueCustomHeaders.map((entry) => String(entry.fieldName))
            );

            const missingEnabledCustomHeaders = enabledProjectCustomFields
              .map((field) => {
                const fieldName = toCustomColumnFieldName(field.label);
                if (existingCustomFieldNames.has(fieldName)) {
                  return null;
                }

                return {
                  fieldName,
                  label: field.label,
                  isSortable: true,
                  isHidden: false,
                  sortDirection: null,
                };
              })
              .filter(
                (entry): entry is NonNullable<typeof entry> => entry != null
              );

            const headerByField = new Map(
              orderedUniqueKnownHeaders.map((entry) => [entry.fieldName, entry])
            );

            const canonicalOverviewFieldNames = new Set<string>(
              OVERVIEW_TABLE_FIELD_ORDER
            );

            const getCanonicalOverviewLabel = (fieldName: string) => {
              const localized =
                getLocalizedMessage(language || "en", fieldName) || fieldName;
              return fieldName === "owner"
                ? getOeOwnerLabel(
                    settings?.configuration?.projectName,
                    localized
                  )
                : localized;
            };

            // Ensure one header per canonical column so header/body indices always align.
            const normalizedBaseHeaders = OVERVIEW_TABLE_FIELD_ORDER.map(
              (fieldName) => {
                const fromConfig = headerByField.get(fieldName);

                return {
                  ...(fromConfig ?? {
                    fieldName,
                    isSortable: true,
                    isHidden: false,
                    sortDirection: null,
                  }),
                  label: getCanonicalOverviewLabel(fieldName),
                };
              }
            );

            const normalizedHeaders = [
              ...normalizedBaseHeaders,
              ...orderedUniqueCustomHeaders,
              ...missingEnabledCustomHeaders,
            ];

            const normalizedHeaderByFieldName = new Map(
              normalizedHeaders.map((entry) => [String(entry.fieldName), entry])
            );

            // Saved table configuration order is the source of truth.
            // Keep that order, then append any missing columns.
            const savedOrderedFieldNames = orderedUniqueHeaders
              .map((entry) => String(entry.fieldName))
              .filter((fieldName) =>
                normalizedHeaderByFieldName.has(fieldName)
              );

            const missingFieldNames = normalizedHeaders
              .map((entry) => String(entry.fieldName))
              .filter(
                (fieldName) => !savedOrderedFieldNames.includes(fieldName)
              );

            const orderedHeadersForRender = [
              ...savedOrderedFieldNames,
              ...missingFieldNames,
            ].map((fieldName) => normalizedHeaderByFieldName.get(fieldName)!);

            const tableHeaderEntries = generateTableHeaderEntriesWithSorting<
              Project & {
                progress: number;
                severity: string;
                status: string;
                degreeOfCompletion: string;
                endDate: string;
                updateStatus: string;
                rating: number;
              }
            >({
              entries: orderedHeadersForRender.map((e) => ({
                ...e,
                fieldName: e.fieldName as keyof (Project & {
                  progress: number;
                  severity: string;
                  status: string;
                  degreeOfCompletion: string;
                  endDate: string;
                  updateStatus: string;
                  rating: number;
                }),
                label: canonicalOverviewFieldNames.has(String(e.fieldName))
                  ? getCanonicalOverviewLabel(String(e.fieldName))
                  : e.label ||
                    getLocalizedMessage(language || "en", `${e.fieldName}`),
              })),
              tableSortData: overviewPluginSortData,
            });

            const headerVisibilityByField: Record<string, boolean> = {
              customId: true,
              name: true,
              owner: isOwnerEnabled,
              numSystems: true,
              numApps: true,
              numITServices: true,
              numDistributionChannels: true,
              updateStatus: true,
            };

            const tableHeaderEntriesWithVisibility = tableHeaderEntries.map(
              (entry) => {
                const isIdColumn = entry.fieldName === "customId";
                const sxObject =
                  entry.sx &&
                  typeof entry.sx === "object" &&
                  !Array.isArray(entry.sx)
                    ? (entry.sx as Record<string, unknown>)
                    : undefined;

                const normalizedSx = isIdColumn
                  ? {
                      ...(sxObject ?? {}),
                      position: "sticky" as const,
                      left: 0,
                      zIndex: 4,
                      backgroundColor: "background.paper",
                    }
                  : sxObject
                    ? (() => {
                        const sxWithoutSticky = { ...sxObject };
                        delete sxWithoutSticky.position;
                        delete sxWithoutSticky.left;
                        delete sxWithoutSticky.right;
                        return sxWithoutSticky;
                      })()
                    : entry.sx;

                return {
                  ...entry,
                  sx: normalizedSx,
                  isHidden:
                    entry?.isHidden ||
                    headerVisibilityByField[entry.fieldName] === false,
                };
              }
            );

            const defaultColumnOrder = tableHeaderEntriesWithVisibility.map(
              (_, index) => index
            );

            const isValidColumnOrder = (order: number[] | undefined) => {
              if (
                !order ||
                order.length !== tableHeaderEntriesWithVisibility.length
              ) {
                return false;
              }

              const expected = new Set(defaultColumnOrder);
              const actual = new Set(order);

              if (actual.size !== expected.size) {
                return false;
              }

              return [...expected].every((index) => actual.has(index));
            };

            const effectiveColumnOrder = isValidColumnOrder(
              overviewPluginColumnOrder
            )
              ? overviewPluginColumnOrder
              : defaultColumnOrder;

            // Seed order once after refresh so drag/reorder state is restored.
            if (!isValidColumnOrder(overviewPluginColumnOrder)) {
              this.app?.patchAppState({
                overviewPluginColumnOrder: effectiveColumnOrder,
              });
            }

            const dynamicTableState = createDynamicTableState({
              app: this.app,
              paginationPrefix: "overviewProjects",
              persistedPagination: overviewTablePagination,
              onPatchPagination: (pagination) => {
                this.app?.patchAppState({
                  overviewTablePagination: pagination,
                });
              },
              items: projectItems,
              filterFn: (entry) =>
                doesProjectPassFilterCriteria(entry, filterData, settings),
            });

            const filteredProjectItems = dynamicTableState.filteredItems;
            const paginatedProjectItems = dynamicTableState.visibleItems;
            const paginationController = dynamicTableState.paginationController;

            return {
              title: `${
                PROJECT_NAMES.find(
                  (name) => name.single === settings?.configuration?.projectName
                )?.plural ?? getLocalizedMessage(language || "en", "project")
              } ${getLocalizedMessage(language || "en", "overview")}`,
              overflow: "visible",
              isSidebarOpen: isSidebarOpen,
              rightSlotOverride: (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <PMButton
                    size="medium"
                    variant="outlined"
                    icon={<Add />}
                    text={`${getLocalizedMessage(language || "en", "addNew")} ${
                      settings?.configuration?.projectName ??
                      getLocalizedMessage(language || "en", "project")
                    }`}
                    onClick={() => {
                      this.app?.navigate(
                        generatePath(APP_ROUTES.projectDialog, {
                          id: "new-project",
                          isFromOverview: "true",
                        })
                      );
                    }}
                  />

                  <SearchAndFilter
                    filterData={filterData}
                    handleChange={(newFilterData) => {
                      paginationController?.resetToFirstPage();

                      this.app?.patchAppState({
                        filterData: newFilterData,
                      });
                    }}
                    clearAllFilters={() => {
                      paginationController?.resetToFirstPage();

                      this.app?.patchAppState({
                        filterData: undefined,
                      });
                    }}
                    options={{
                      oe: {
                        isEnabled: true,
                        options: oeFilterOptions,
                        label:
                          settings?.configuration?.projectName ||
                          getLocalizedMessage(language || "en", "project") ||
                          "OE",
                      },
                      owner: {
                        isEnabled: isOwnerEnabled,
                        options: ownerFilterOptions,
                        label: "CISO",
                      },
                      updateStatus: {
                        isEnabled: true,
                        options: updateStatusFilterOptions,
                      },
                    }}
                  ></SearchAndFilter>
                  {canUserPerformOperation({
                    userGroups: userProfile?.groups || [],
                    lowestLevelAccessRequiredToPerformThisOperation: "admin",
                    client: settings?.configuration?.client,
                    tool: settings?.configuration?.tool,
                  }) &&
                    (this.app?.getAppState().overviewTableConfiguration
                      ?.isEditMode ? (
                      <Box
                        sx={{
                          display: "flex",
                          p: 0,
                          gap: 0,
                        }}
                      >
                        <Button
                          sx={{
                            borderRadius: "100%",
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            padding: 0,
                          }}
                          onClick={async () => {
                            if (!this.app) {
                              return;
                            }

                            await saveTableConfigurationAndExitEditMode({
                              app: this.app,
                              configKey: "overviewTableConfiguration",
                              columnOrderKey: "overviewPluginColumnOrder",
                              componentId: "projectsTableComponent",
                              applyColumnOrder: applyColumnOrderToHeaders,
                            });
                          }}
                        >
                          <Save sx={{ height: "20px", width: "20px" }} />
                        </Button>
                        <Button
                          sx={{
                            borderRadius: "100%",
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            padding: 0,
                          }}
                          onClick={async () => {
                            if (!this.app) {
                              return;
                            }

                            const tableConfiguration =
                              this.app.getAppState().overviewTableConfiguration;

                            if (!tableConfiguration) {
                              return;
                            }

                            if (tableConfiguration.isDirty) {
                              setTableConfigurationConfirmationPopup(
                                this.app,
                                "overviewTableConfiguration",
                                "abort"
                              );

                              return;
                            }

                            await restorePersistedTableConfiguration({
                              app: this.app,
                              configKey: "overviewTableConfiguration",
                              columnOrderKey: "overviewPluginColumnOrder",
                              componentId: "projectsTableComponent",
                            });
                          }}
                        >
                          <Close sx={{ height: "20px", width: "20px" }} />
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        sx={{
                          borderRadius: "100%",
                          width: "40px",
                          height: "40px",
                          minWidth: "40px",
                          padding: 0,
                        }}
                        onClick={() => {
                          if (!this.app) {
                            return;
                          }

                          beginTableConfigurationEdit(
                            this.app,
                            "overviewTableConfiguration",
                            tableHeaderEntries
                          );
                        }}
                      >
                        <Settings sx={{ height: "20px", width: "20px" }} />
                      </Button>
                    ))}
                </Box>
              ),
              showInfoIcon: false,
              showMenuIcon: false,
              allowColumnReorder: true,
              bottomRightSlotOverride: (
                <DynamicTablePagination
                  paginationController={paginationController}
                  totalItems={filteredProjectItems.length}
                />
              ),
              isEditMode: overviewTableConfiguration?.isEditMode ?? false,
              colOrder: effectiveColumnOrder,
              setColOrder: (order) =>
                this.app?.patchAppState({ overviewPluginColumnOrder: order }),
              data: paginatedProjectItems.map((entry) => {
                // Translate the status
                const updateStatusKey =
                  entry._calculated?.updateStatus?.status?.toLowerCase();

                const translatedStatus =
                  (updateStatusKey
                    ? getLocalizedMessage(language || "en", updateStatusKey)
                    : undefined) ??
                  entry._calculated?.updateStatus?.status ??
                  "";

                const projectEditLock =
                  lockedProjects?.[`project-${entry.id}`]?.edit;
                const isEditLocked =
                  projectEditLock?.isLocked &&
                  projectEditLock?.lockedBy !== userProfile?.email;

                const isCompleted =
                  entry._calculated?.completionDate &&
                  dayjs(entry._calculated.completionDate).isValid() &&
                  dayjs(entry._calculated.completionDate).isBefore(dayjs());

                const projectCustomFields = (
                  entry as Project & {
                    customFields?: {
                      value?: unknown;
                      customFieldDefinition?: {
                        label?: string;
                      };
                    }[];
                  }
                ).customFields;

                const customFieldValueByFieldName = new Map<string, unknown>();
                const customFieldValueByLabel = new Map<string, unknown>();

                for (const customFieldValue of projectCustomFields ?? []) {
                  const fieldName = toCustomColumnFieldName(
                    customFieldValue.customFieldDefinition?.label
                  );
                  const labelKey = String(
                    customFieldValue.customFieldDefinition?.label ?? ""
                  )
                    .trim()
                    .toLowerCase();
                  const nextValue = customFieldValue.value;
                  const currentValue =
                    customFieldValueByFieldName.get(fieldName);
                  const currentLabelValue =
                    customFieldValueByLabel.get(labelKey);

                  const hasMeaningfulValue =
                    nextValue != null && String(nextValue).trim() !== "";
                  const currentHasMeaningfulValue =
                    currentValue != null && String(currentValue).trim() !== "";
                  const labelHasMeaningfulValue =
                    currentLabelValue != null &&
                    String(currentLabelValue).trim() !== "";

                  // Keep whichever row has an actual value to avoid null placeholder rows
                  // overriding real persisted values for the same custom field.
                  if (!currentHasMeaningfulValue || hasMeaningfulValue) {
                    customFieldValueByFieldName.set(fieldName, nextValue);
                  }

                  if (
                    labelKey &&
                    (!labelHasMeaningfulValue || hasMeaningfulValue)
                  ) {
                    customFieldValueByLabel.set(labelKey, nextValue);
                  }
                }

                const baseCellByFieldName = new Map<
                  string,
                  Record<string, unknown>
                >([
                  [
                    "customId",
                    {
                      component: TextCardCell,
                      body: String(entry.customId),
                      cardProps: {
                        sx: {
                          position: "sticky" as const,
                          left: 0,
                          zIndex: 3,
                          minWidth: 120,
                          width: 120,
                          backgroundColor: "background.paper",
                        },
                      },
                      bodyProps: {
                        sx: {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      },
                    },
                  ],
                  [
                    "name",
                    {
                      component: TextCardCell,
                      body: String(entry.name),
                      cutBodyAfter: 51,
                      cardProps: {
                        sx: {
                          zIndex: 3,
                          minWidth: 300,
                          width: 300,
                          backgroundColor: "background.paper",
                        },
                      },
                      tooltipProps: {
                        placement: "right",
                        slotProps: {
                          tooltip: {
                            sx: {
                              backgroundColor: "#fff",
                              color: SECONDARY_TEXT_COLOR,
                              border: "1px solid",
                              borderColor: "background.paper",
                              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                              fontSize: "14px",
                              fontWeight: 400,
                              padding: "12px 16px",
                              borderRadius: "8px",
                            },
                          },
                        },
                      },
                    },
                  ],
                  [
                    "owner",
                    {
                      component: TextCardCell,
                      body: String(entry.owner),
                      cutBodyAfter: 20,
                      tooltipProps: {
                        placement: "right",
                        slotProps: {
                          tooltip: {
                            sx: {
                              backgroundColor: "#fff",
                              color: SECONDARY_TEXT_COLOR,
                              border: "1px solid",
                              borderColor: "background.paper",
                              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                              fontSize: "14px",
                              fontWeight: 400,
                              padding: "12px 16px",
                              borderRadius: "8px",
                            },
                          },
                        },
                      },
                    },
                  ],
                  [
                    "numSystems",
                    {
                      component: ChipCell,
                      body: String((entry.reportingItems ?? []).length),
                      chipProps,
                    },
                  ],
                  [
                    "numApps",
                    {
                      component: ChipCell,
                      body: String(
                        (entry.reportingItems ?? []).filter(
                          (item) =>
                            !item.isChannelHeader &&
                            item.basicItem?.recordType ===
                              APP_RECORD_TYPES.BUSINESS_APPLICATION
                        ).length
                      ),
                      chipProps,
                    },
                  ],
                  [
                    "numITServices",
                    {
                      component: ChipCell,
                      body: String(
                        (entry.reportingItems ?? []).filter(
                          (item) =>
                            !item.isChannelHeader &&
                            item.basicItem?.recordType ===
                              APP_RECORD_TYPES.IT_SERVICE
                        ).length
                      ),
                      chipProps,
                    },
                  ],
                  [
                    "numDistributionChannels",
                    {
                      component: ChipCell,
                      body: String(
                        new Set(
                          (entry.reportingItems ?? [])
                            .filter((item) => !item.isChannelHeader)
                            .map((item) => item.distributionChannel)
                            .filter((ch) => ch != null)
                        ).size
                      ),
                      chipProps,
                    },
                  ],
                  [
                    "updateStatus",
                    {
                      component: TableUpdateStatusCell,
                      body: isCompleted
                        ? getLocalizedMessage(language || "en", "completed") ||
                          "Completed"
                        : translatedStatus,
                      icon: isCompleted
                        ? undefined
                        : entry._calculated?.updateStatus?.icon,
                      muiIcon: isCompleted ? "CheckCircleOutlined" : undefined,
                      chipColor: isCompleted
                        ? COLORS[0]
                        : (entry._calculated?.updateStatus?.color ?? COLORS[0]),
                      chipProps: {
                        sx: {
                          color: isCompleted
                            ? CONTRAST_TEXT_COLORS[0]
                            : (entry._calculated?.updateStatus
                                ?.contrastTextColor ?? CONTRAST_TEXT_COLORS[0]),
                          fontWeight:
                            translatedStatus === "Not selected" ? 400 : 500,
                        },
                      },
                      tooltip: isCompleted
                        ? `${
                            getLocalizedMessage(
                              language || "en",
                              "completedOn"
                            ) || "Completed on"
                          } ${formatDate(
                            entry._calculated?.completionDate,
                            language
                          )}`
                        : `${translatedStatus} ${
                            entry._calculated?.lastUpdatedAt
                              ? `${
                                  getLocalizedMessage(
                                    language || "en",
                                    "asOf"
                                  ) || "as of"
                                } ${formatDateTime(
                                  entry._calculated?.lastUpdatedAt,
                                  language
                                )}`
                              : ""
                          }${
                            entry._calculated?.lastUpdatedBy
                              ? ` ${
                                  getLocalizedMessage(language || "en", "by") ||
                                  "by"
                                } ${entry._calculated?.lastUpdatedBy}`
                              : ""
                          }`,
                    },
                  ],
                ]);

                const orderedColumnProps = tableHeaderEntriesWithVisibility.map(
                  (header) => {
                    const fieldName = String(header.fieldName);

                    if (fieldName.startsWith("customField-")) {
                      const customFieldDefinition =
                        customFieldDefinitionByFieldName.get(fieldName);

                      const rawValue =
                        customFieldValueByFieldName.get(fieldName) ??
                        customFieldValueByLabel.get(
                          String(header.label ?? "")
                            .trim()
                            .toLowerCase()
                        );

                      const baseState = {
                        isHidden: header.isHidden,
                        isVisible: header.isVisible,
                      };

                      if (customFieldDefinition?.type === "Date") {
                        return {
                          component: DateCell,
                          date: rawValue ? formatDate(String(rawValue)) : "N/A",
                          ...baseState,
                        };
                      }

                      if (customFieldDefinition?.type === "Select") {
                        const optionColor =
                          customFieldDefinition.options?.find(
                            (option) => option.name === String(rawValue ?? "")
                          )?.color ?? RECORD_TYPE_BADGE_COLORS.itService;

                        return {
                          component: BadgeCell,
                          body: String(rawValue ?? "n/a"),
                          color: optionColor,
                          textColor: getContrastColor(optionColor),
                          ...baseState,
                        };
                      }

                      return {
                        component: TextCardCell,
                        body: String(rawValue ?? "n/a"),
                        cutBodyAfter:
                          customFieldDefinition?.type === "Text"
                            ? 51
                            : undefined,
                        ...baseState,
                      };
                    }

                    const baseCell = baseCellByFieldName.get(fieldName);

                    return {
                      ...(baseCell ?? {
                        component: TextCardCell,
                        body: "n/a",
                      }),
                      isHidden: header.isHidden,
                      isVisible: header.isVisible,
                    };
                  }
                ) as unknown as DerivePMTableProps<
                  typeof projectsTable
                >["data"][number]["columnProps"];

                return {
                  columnProps: orderedColumnProps,
                  rowProps: {
                    cardProps: { sx: { cursor: "pointer" } },
                    onClick: () => {
                      const projectId = entry.id;
                      this.app?.navigate(
                        generatePath(APP_ROUTES.report, {
                          id: projectId,
                          viewname: "dashboard",
                        })
                      );
                    },
                    lockMenu: isEditLocked ?? false,
                    menuTooltip: getLockedByMessage(
                      "project",
                      language,
                      projectEditLock
                    ),
                    onMenuOpen: canUserPerformOperation({
                      userGroups: userProfile?.groups ?? [],
                      object: "project",
                      operation: "edit",
                      entityId: entry.id,
                      lowestLevelAccessRequiredToPerformThisOperation:
                        entry.userCreatedProject ? "project" : "program",
                      client: settings?.configuration?.client,
                      tool: settings?.configuration?.tool,
                    })
                      ? {
                          onEdit: async () => {
                            const projectId = entry.id;

                            projectId != null &&
                              resourceLockPlugin?.lock(`project-${projectId}`);
                            this.app?.patchAppState({
                              editForm: {
                                currentProject: entry,
                              },
                            });
                            projectId &&
                              this.app?.navigate(
                                generatePath(APP_ROUTES.projectDialog, {
                                  id: projectId,
                                  isFromOverview: "true",
                                })
                              );
                            this.app?.patchAppState({
                              overlay: await fetchOverlay(),
                              overview: await fetchOverview(),
                              editForm: {
                                currentProject: entry,
                              },
                            });
                          },
                          onDelete: async () => {
                            const projectId = entry.id;

                            // show confirmation dialog and set pending project id
                            this.app?.patchAppState({
                              showDeleteProjectDialog: true,
                              pendingDeleteProjectId: projectId,
                            });
                          },
                        }
                      : undefined,
                  },
                };
              }),
              tableHeaderEntries: tableHeaderEntriesWithVisibility,
              language,
            };
          }
        );

        projectsTableComponent.on("reorderColumns", () => {
          if (!this.app) {
            return;
          }

          markTableConfigurationDirty(this.app, "overviewTableConfiguration");
        });

        projectsTableComponent.on("addColumn", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          markTableConfigurationDirtyFromColumnPayload(
            this.app,
            "overviewTableConfiguration",
            event.payload
          );
        });

        projectsTableComponent.on("deleteColumn", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          markTableConfigurationDirtyFromColumnPayload(
            this.app,
            "overviewTableConfiguration",
            event.payload
          );
        });

        const itSystemsOverviewTableDefinition = [
          { component: TextCardCell },
          { component: TextCardCell },
          { component: BadgeCell },
          { component: TextCardCell },
          { component: TextCardCell },
          { component: BadgeCell },
          { component: BadgeCell },
          { component: TextCardCell },
          { component: TextCardCell },
          { component: TextCardCell },
          { component: BadgeCell },
          { component: OpenAssessmentCell },
        ];

        const itSystemsOverviewTable = new PMTable<
          typeof itSystemsOverviewTableDefinition
        >(itSystemsOverviewTableDefinition);

        const itSystemsOverviewTableComponent = view.addComponent<
          DerivePMTableProps<typeof itSystemsOverviewTable>,
          PMTableEventNames,
          PMTableEventPayload
        >({
          component: itSystemsOverviewTable,
          layoutProps: {
            componentId: "itSystemsOverviewTableComponent",
            labels: ["table", "tracking items", "overview"],
            description:
              "Shows a table view of tracking items across all projects.",
            title: "Overview",
          },
        });

        itSystemsOverviewTableComponent.on("sort", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          const payload = event.payload as PMTableSortEventPayload<
            keyof (BasicItem | ReportingItem)
          >;
          const state = this.app.getAppState();
          let direction: SortDirection = "ASC";

          if (
            state.reportPluginSortData?.key === payload?.sort?.key &&
            state.reportPluginSortData?.direction === "ASC"
          ) {
            direction = "DESC";
          }

          this.app.patchAppState({
            reportPluginSortData: {
              key: payload?.sort?.key,
              direction,
            },
          });
        });

        itSystemsOverviewTableComponent.mapState(
          ({
            overview,
            settings,
            reportPluginSortData,
            language,
            overviewItSystemsTablePagination,
            overviewItSystemsColumnOrder,
            itSystemsOverviewTableConfiguration,
            itSystemsOverviewFilterData,
            userProfile,
            lockDetails,
            showCreateTrackingItemOeDialog,
            selectedOeIdForNewTrackingItem,
          }) => {
            const resourceLockPlugin =
              this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
                "ResourceLockPlugin"
              );

            // Permission is entity-scoped (per OE), so unlike a single global
            // check, each project must be checked individually: a "project"-role
            // user only has edit/item access to their own OEs, not to entityId "".
            const oeOptionsForNewTrackingItem = [...(overview?.projects ?? [])]
              .filter(
                (project): project is Project & { id: string } => !!project.id
              )
              .filter((project) =>
                canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  operation: "edit",
                  object: "item",
                  entityId: project.id,
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                })
              )
              .map((project) => ({
                id: project.id,
                label: `${
                  project.customId ? project.customId + " - " : ""
                }${project.name ?? ""}`,
              }))
              .sort((a, b) => a.label.localeCompare(b.label));

            const allReportingItems = (overview?.projects ?? [])
              .flatMap((project) =>
                (project.reportingItems ?? []).map((item) => ({
                  ...item,
                  project,
                  customId: item.basicItem?.customId,
                  dcRelevantOe: getDcRelevantOeDisplay(item),
                  _degreeOfCompletionOrder: resolveStatusOrder(
                    settings?.configuration?.degreeOfCompletionDefinition,
                    item.degreeOfCompletion
                  ),
                }))
              )
              .filter((item) => !item.isChannelHeader) as ReportingItem[];

            const effectiveSortData: Parameters<typeof sortReportingItems>[1] =
              (reportPluginSortData as Parameters<
                typeof sortReportingItems
              >[1]) ?? {
                key: "customId",
                direction: "ASC",
              };

            const reportingItems = sortReportingItems(
              [...allReportingItems],
              effectiveSortData
            );

            const ownerFilterOptions = [
              ...new Set(
                reportingItems
                  .map((item) => item.basicItem?.owner?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const distributionChannelFilterOptions = [
              ...new Set(
                reportingItems
                  .flatMap((item) => item.distributionChannel ?? [])
                  .map(
                    (channelId) =>
                      settings?.configuration?.distributionChannelDefinition?.find(
                        (channel) => channel.id === channelId
                      )?.label ?? channelId
                  )
                  .map((label) => label?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const lifeCycleFilterOptions = [
              ...new Set(
                reportingItems
                  .map((item) => item.basicItem?.lifeCycle?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const criticalityFilterOptions = [
              ...new Set(
                reportingItems
                  .map((item) => item.basicItem?.criticality?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const reviewStatusFilterOptions = [
              ...new Set(
                reportingItems
                  .map((item) => item.degreeOfCompletion?.trim())
                  .filter((value): value is string => Boolean(value))
              ),
            ].sort((a, b) => a.localeCompare(b));

            const rawTableHeaderEntries =
              itSystemsOverviewTableConfiguration?.tableHeaderEntries ?? [];

            const orderedUniqueHeaders = rawTableHeaderEntries.filter(
              (entry, index, source) =>
                source.findIndex((e) => e.fieldName === entry.fieldName) ===
                index
            );

            const orderedUniqueKnownHeaders = orderedUniqueHeaders.filter(
              (entry) =>
                IT_SYSTEMS_OVERVIEW_TABLE_FIELD_ORDER.includes(
                  entry.fieldName as (typeof IT_SYSTEMS_OVERVIEW_TABLE_FIELD_ORDER)[number]
                )
            );

            const headerByField = new Map(
              orderedUniqueKnownHeaders.map((entry) => [entry.fieldName, entry])
            );

            // Always recompute the label instead of trusting a persisted
            // config's `label`, since old saved table configurations may
            // still carry stale header text (e.g. "System Name", "ISO",
            // "Owner") from before these headers were renamed.
            const getItSystemsOverviewHeaderLabel = (
              fieldName: (typeof IT_SYSTEMS_OVERVIEW_TABLE_FIELD_ORDER)[number]
            ) =>
              fieldName === "recordType"
                ? getSystemTypeHeaderLabel(language)
                : fieldName === "open"
                  ? getLocalizedMessage(language || "en", "open") || "Open"
                  : fieldName === "dcRelevantOe"
                    ? "DC Relevant (OE)"
                    : fieldName === "owner"
                      ? IT_SYSTEMS_OVERVIEW_OWNER_LABEL
                      : fieldName === "name"
                        ? getLocalizedMessage(language || "en", "name") ||
                          "Name"
                        : getLocalizedMessage(
                            language || "en",
                            REPORT_TABLE_FIELD_LOCALE_KEYS[fieldName]
                          ) || fieldName;

            const normalizedBaseHeaders =
              IT_SYSTEMS_OVERVIEW_TABLE_FIELD_ORDER.map((fieldName) => {
                const fromConfig = headerByField.get(fieldName);

                return {
                  ...(fromConfig ?? {
                    fieldName,
                    isSortable: fieldName !== "open",
                    isHidden: false,
                    sortDirection: null,
                    sx: {
                      width: "200px",
                    },
                  }),
                  label: getItSystemsOverviewHeaderLabel(fieldName),
                };
              });

            // Unlike the OE/projects overview table, this table's row cells
            // (`columnProps` below) are a hand-written array in the fixed
            // `IT_SYSTEMS_OVERVIEW_TABLE_FIELD_ORDER` sequence, not derived
            // from `tableHeaderEntries` by field name. The renderer
            // (`PMTable`) zips headers with row cells by array index, so the
            // header order fed into it must always be that same canonical
            // order — reordering it based on a saved config (e.g. an older
            // config that predates a column and therefore omits it, pushing
            // it to the end) shifts every following header out of alignment
            // with its cell (e.g. "Owner" rendering the "System Type" cell's
            // value). Per-field overrides (label/isHidden/isSortable/sort)
            // still come from the saved config via `normalizedBaseHeaders`;
            // only the *order* of the array itself must stay canonical. User
            // reordering is handled separately and safely via `colOrder`
            // (see hipsquare/pimo#2672).
            const orderedHeadersForRender = normalizedBaseHeaders;

            const dynamicTableState = createDynamicTableState({
              app: this.app,
              paginationPrefix: "overviewItSystems",
              persistedPagination: overviewItSystemsTablePagination,
              onPatchPagination: (pagination) => {
                this.app?.patchAppState({
                  overviewItSystemsTablePagination: pagination,
                });
              },
              items: reportingItems,
              filterFn: (item) =>
                doesItSystemsOverviewItemPassFilterCriteria(
                  item,
                  itSystemsOverviewFilterData,
                  settings
                ),
            });

            const filteredReportingItems = dynamicTableState.filteredItems;
            const paginatedReportingItems = dynamicTableState.visibleItems;
            const paginationController = dynamicTableState.paginationController;

            // Size the Review Status column to the labels actually on screen, so it hugs Open when there's no long badge like "Ready to Submit".
            const degreeColumnWidthPx = getStatusBadgeColumnWidth(
              paginatedReportingItems.map((item) => item.degreeOfCompletion)
            );

            const tableHeaderEntries = generateTableHeaderEntriesWithSorting<
              BasicItem & ReportingItem
            >({
              entries: orderedHeadersForRender.map((entry) => ({
                ...entry,
                fieldName: entry.fieldName as keyof (BasicItem & ReportingItem),
                label:
                  entry.label ||
                  (entry.fieldName === "recordType"
                    ? getSystemTypeHeaderLabel(language)
                    : entry.fieldName === "dcRelevantOe"
                      ? "DC Relevant (OE)"
                      : entry.fieldName === "owner"
                        ? IT_SYSTEMS_OVERVIEW_OWNER_LABEL
                        : entry.fieldName === "name"
                          ? getLocalizedMessage(language || "en", "name")
                          : getLocalizedMessage(
                              language || "en",
                              REPORT_TABLE_FIELD_LOCALE_KEYS[
                                entry.fieldName as Exclude<
                                  (typeof IT_SYSTEMS_OVERVIEW_TABLE_FIELD_ORDER)[number],
                                  | "recordType"
                                  | "dcRelevantOe"
                                  | "owner"
                                  | "name"
                                >
                              ]
                            )) ||
                  String(entry.fieldName),
              })),
              tableSortData: effectiveSortData,
            });

            const hasVisibleOpenColumn = tableHeaderEntries.some(
              (entry) =>
                String(entry.fieldName) === "open" &&
                entry.isVisible !== false &&
                !entry.isHidden
            );

            const tableHeaderEntriesWithVisibility = tableHeaderEntries.map(
              (entry) => {
                const isIdColumn = entry.fieldName === "customId";
                const isArrayFieldColumn = [
                  "processSteps",
                  "applicationCapabilities",
                  "technicalExposure",
                ].includes(String(entry.fieldName));
                const isDegreeOfCompletionColumn =
                  String(entry.fieldName) === "degreeOfCompletion";
                const isOpenColumn = String(entry.fieldName) === "open";
                const isDcRelevantOeColumn =
                  String(entry.fieldName) === "dcRelevantOe";
                const sxObject =
                  entry.sx &&
                  typeof entry.sx === "object" &&
                  !Array.isArray(entry.sx)
                    ? (entry.sx as Record<string, unknown>)
                    : undefined;

                const normalizedSx = isIdColumn
                  ? {
                      ...(sxObject ?? {}),
                      position: "sticky" as const,
                      left: 0,
                      zIndex: 4,
                      backgroundColor: "background.paper",
                    }
                  : sxObject
                    ? (() => {
                        const sxWithoutSticky = { ...sxObject };
                        delete sxWithoutSticky.position;
                        delete sxWithoutSticky.left;
                        delete sxWithoutSticky.right;
                        return sxWithoutSticky;
                      })()
                    : entry.sx;

                const normalizedSxWithFlexibleApplicationCapabilities =
                  isArrayFieldColumn
                    ? {
                        ...(normalizedSx &&
                        typeof normalizedSx === "object" &&
                        !Array.isArray(normalizedSx)
                          ? (normalizedSx as Record<string, unknown>)
                          : {}),
                        width: "200px",
                      }
                    : isDcRelevantOeColumn
                      ? {
                          ...(normalizedSx &&
                          typeof normalizedSx === "object" &&
                          !Array.isArray(normalizedSx)
                            ? (normalizedSx as Record<string, unknown>)
                            : {}),
                          // Fixed width so the sort/filter icon in this
                          // header never gets cut off/covered by the
                          // adjacent sticky "Review Status" column.
                          width: "170px",
                        }
                      : isDegreeOfCompletionColumn
                        ? {
                            ...(normalizedSx &&
                            typeof normalizedSx === "object" &&
                            !Array.isArray(normalizedSx)
                              ? (normalizedSx as Record<string, unknown>)
                              : {}),
                            width: `${degreeColumnWidthPx}px`,
                            position: "sticky" as const,
                            right: hasVisibleOpenColumn
                              ? `${IT_SYSTEMS_OPEN_COLUMN_WIDTH_PX}px`
                              : 0,
                            zIndex: 4,
                            backgroundColor: "background.paper",
                          }
                        : isOpenColumn
                          ? {
                              ...(normalizedSx &&
                              typeof normalizedSx === "object" &&
                              !Array.isArray(normalizedSx)
                                ? (normalizedSx as Record<string, unknown>)
                                : {}),
                              width: `${IT_SYSTEMS_OPEN_COLUMN_WIDTH_PX}px`,
                              position: "sticky" as const,
                              right: 0,
                              zIndex: 5,
                              backgroundColor: "background.paper",
                            }
                          : normalizedSx;

                return {
                  ...entry,
                  label: isOpenColumn ? "" : entry.label,
                  sx: normalizedSxWithFlexibleApplicationCapabilities,
                };
              }
            );

            const defaultColumnOrder = tableHeaderEntriesWithVisibility.map(
              (_, index) => index
            );

            const isValidColumnOrder = (order: number[] | undefined) => {
              if (
                !order ||
                order.length !== tableHeaderEntriesWithVisibility.length
              ) {
                return false;
              }

              const expected = new Set(defaultColumnOrder);
              const actual = new Set(order);

              if (actual.size !== expected.size) {
                return false;
              }

              return [...expected].every((index) => actual.has(index));
            };

            const effectiveColumnOrder = isValidColumnOrder(
              overviewItSystemsColumnOrder
            )
              ? overviewItSystemsColumnOrder
              : defaultColumnOrder;

            if (!isValidColumnOrder(overviewItSystemsColumnOrder)) {
              this.app?.patchAppState({
                overviewItSystemsColumnOrder: effectiveColumnOrder,
              });
            }

            const trackingItemsOverviewActionsMenu = (
              <ReportTableActionsMenu
                canCreate={oeOptionsForNewTrackingItem.length > 0}
                createLabel={
                  getLocalizedMessage(language || "en", "createButton") ||
                  "Create"
                }
                createTooltip={`${
                  getLocalizedMessage(language || "en", "addNew") || "Add New"
                } ${getItemCreationDisplayName(
                  settings?.configuration?.itemName
                )}`}
                downloadLabel={
                  getLocalizedMessage(language || "en", "downloadButton") ||
                  "Download"
                }
                downloadTooltip={
                  getLocalizedMessage(
                    language || "en",
                    "downloadBusinessApps"
                  ) || "Download Business Apps"
                }
                onCreate={() => {
                  this.app?.patchAppState({
                    showCreateTrackingItemOeDialog: true,
                    selectedOeIdForNewTrackingItem: undefined,
                  });
                }}
                onDownload={() => {
                  const itemIds = getBusinessAppExportItemIds(
                    reportingItems,
                    itSystemsOverviewFilterData,
                    settings
                  );
                  const activeFilterLabels = getActiveBusinessAppFilterLabels(
                    itSystemsOverviewFilterData
                  );

                  void downloadBusinessAppExport(
                    itemIds,
                    activeFilterLabels
                  ).then((success) => {
                    if (success) {
                      return;
                    }

                    this.app?.patchAppState({
                      snackbar: {
                        message:
                          getLocalizedMessage(
                            language || "en",
                            "downloadBusinessAppsError"
                          ) ||
                          "Failed to download Business Apps. Please try again.",
                        severity: "error",
                        open: true,
                        type: "snackbar",
                      },
                    });
                  });
                }}
              />
            );

            return {
              title:
                getLocalizedMessage(
                  language || "en",
                  "overviewPlugin.itSystemsOverviewTitle"
                ) || "Overview",
              overflow: "visible",
              showInfoIcon: false,
              showMenuIcon: false,
              rightSlotOverride: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PMPopUp
                    open={!!showCreateTrackingItemOeDialog}
                    title={
                      getLocalizedMessage(
                        language || "en",
                        "dialog.title.selectOe",
                        {
                          projectName:
                            settings?.configuration?.projectName ?? "OE",
                        }
                      ) ||
                      `Select ${settings?.configuration?.projectName ?? "OE"}`
                    }
                    description={
                      getLocalizedMessage(
                        language || "en",
                        "dialog.message.selectOeForNewItem",
                        {
                          projectName:
                            settings?.configuration?.projectName ?? "OE",
                          itemName: getItemCreationDisplayName(
                            settings?.configuration?.itemName
                          ),
                        }
                      ) ||
                      `Select the ${
                        settings?.configuration?.projectName ?? "OE"
                      } to create the new ${getItemCreationDisplayName(
                        settings?.configuration?.itemName
                      )} in`
                    }
                    onClose={() => {
                      this.app?.patchAppState({
                        showCreateTrackingItemOeDialog: false,
                        selectedOeIdForNewTrackingItem: undefined,
                      });
                    }}
                    maxWidth="sm"
                    cancelButtonText={
                      getLocalizedMessage(
                        language || "en",
                        "dialog.button.cancel"
                      ) || "Cancel"
                    }
                    cancelButtonFireEvent={() => {
                      this.app?.patchAppState({
                        showCreateTrackingItemOeDialog: false,
                        selectedOeIdForNewTrackingItem: undefined,
                      });
                    }}
                    cancelButtonColor="white"
                    confirmationButtonColor="blue"
                    confirmationButtonDisabled={!selectedOeIdForNewTrackingItem}
                    confirmationButtonText={
                      getLocalizedMessage(
                        language || "en",
                        "dialog.button.confirm"
                      ) || "Confirm"
                    }
                    confirmationButtonFireEvent={() => {
                      const oeId = selectedOeIdForNewTrackingItem;

                      this.app?.patchAppState({
                        showCreateTrackingItemOeDialog: false,
                        selectedOeIdForNewTrackingItem: undefined,
                      });

                      if (oeId) {
                        this.app?.navigate(
                          generatePath(APP_ROUTES.trackingItemDialog, {
                            id: oeId,
                            "tracking-item-id": "new-tracking-item",
                            viewname: "dashboard",
                          })
                        );
                      }
                    }}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel id="create-tracking-item-oe-select-label">
                        {settings?.configuration?.projectName ?? "OE"}
                      </InputLabel>
                      <Select
                        labelId="create-tracking-item-oe-select-label"
                        label={settings?.configuration?.projectName ?? "OE"}
                        value={selectedOeIdForNewTrackingItem ?? ""}
                        onChange={(event) => {
                          this.app?.patchAppState({
                            selectedOeIdForNewTrackingItem:
                              event.target.value || undefined,
                          });
                        }}
                      >
                        {oeOptionsForNewTrackingItem.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </PMPopUp>
                  <SearchAndFilter
                    filterData={itSystemsOverviewFilterData}
                    hideProminentInMoreFilters
                    handleChange={(newFilterData) => {
                      paginationController?.resetToFirstPage();

                      this.app?.patchAppState({
                        itSystemsOverviewFilterData: newFilterData,
                      });
                    }}
                    clearAllFilters={() => {
                      paginationController?.resetToFirstPage();

                      this.app?.patchAppState({
                        itSystemsOverviewFilterData: undefined,
                      });
                    }}
                    options={{
                      owner: {
                        isEnabled: true,
                        options: ownerFilterOptions,
                        label:
                          getLocalizedMessage(language || "en", "owner") ||
                          "Owner",
                      },
                      distributionChannel: {
                        isEnabled: true,
                        options: distributionChannelFilterOptions,
                        label:
                          getLocalizedMessage(
                            language || "en",
                            "distributionChannel"
                          ) || "Distribution Channel",
                      },
                      lifeCycle: {
                        isEnabled: true,
                        options: lifeCycleFilterOptions,
                        label:
                          getLocalizedMessage(
                            language || "en",
                            "lifeCycleStatus"
                          ) || "Life Cycle Status",
                      },
                      criticality: {
                        isEnabled: true,
                        options: criticalityFilterOptions,
                        label:
                          getLocalizedMessage(
                            language || "en",
                            "criticality"
                          ) || "Criticality",
                      },
                      reviewStatus: {
                        isEnabled: true,
                        options: reviewStatusFilterOptions,
                        label:
                          getLocalizedMessage(
                            language || "en",
                            "Review Status"
                          ) || "Review Status",
                      },
                    }}
                  />
                  {/* Visibility mirrors the OE picker options: a "project"-role
                      user only sees this if they have edit/item access to at
                      least one OE (checked per-entityId above), since there is
                      no single entityId to check against on this cross-OE page. */}
                  {trackingItemsOverviewActionsMenu}
                  {canUserPerformOperation({
                    userGroups: userProfile?.groups || [],
                    lowestLevelAccessRequiredToPerformThisOperation: "admin",
                    client: settings?.configuration?.client,
                    tool: settings?.configuration?.tool,
                  }) &&
                    (itSystemsOverviewTableConfiguration?.isEditMode ? (
                      <Box sx={{ display: "flex", p: 0, gap: 0 }}>
                        <Button
                          sx={{
                            borderRadius: "100%",
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            padding: 0,
                          }}
                          onClick={async () => {
                            if (!this.app) {
                              return;
                            }

                            await saveTableConfigurationAndExitEditMode({
                              app: this.app,
                              configKey: "itSystemsOverviewTableConfiguration",
                              columnOrderKey: "overviewItSystemsColumnOrder",
                              componentId: "itSystemsOverviewTableComponent",
                              applyColumnOrder:
                                applyColumnOrderToHeadersForReportPlugin,
                            });
                          }}
                        >
                          <Save sx={{ height: "20px", width: "20px" }} />
                        </Button>
                        <Button
                          sx={{
                            borderRadius: "100%",
                            width: "40px",
                            height: "40px",
                            minWidth: "40px",
                            padding: 0,
                          }}
                          onClick={async () => {
                            if (!this.app) {
                              return;
                            }

                            await restorePersistedTableConfiguration({
                              app: this.app,
                              configKey: "itSystemsOverviewTableConfiguration",
                              columnOrderKey: "overviewItSystemsColumnOrder",
                              componentId: "itSystemsOverviewTableComponent",
                            });
                          }}
                        >
                          <Close sx={{ height: "20px", width: "20px" }} />
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        sx={{
                          borderRadius: "100%",
                          width: "40px",
                          height: "40px",
                          minWidth: "40px",
                          padding: 0,
                        }}
                        onClick={() => {
                          if (!this.app) {
                            return;
                          }

                          beginTableConfigurationEdit(
                            this.app,
                            "itSystemsOverviewTableConfiguration",
                            tableHeaderEntriesWithVisibility
                          );
                        }}
                      >
                        <Settings sx={{ height: "20px", width: "20px" }} />
                      </Button>
                    ))}
                </Box>
              ),
              allowColumnReorder: true,
              bottomRightSlotOverride: (
                <DynamicTablePagination
                  paginationController={paginationController}
                  totalItems={filteredReportingItems.length}
                />
              ),
              isEditMode: itSystemsOverviewTableConfiguration?.isEditMode,
              colOrder: effectiveColumnOrder,
              setColOrder: (order) =>
                this.app?.patchAppState({
                  overviewItSystemsColumnOrder: order,
                }),
              data: paginatedReportingItems.map((item) => {
                const distributionChannelIds = item.distributionChannel ?? [];
                const distributionChannelLabel =
                  distributionChannelIds.length > 0
                    ? distributionChannelIds
                        .map(
                          (chId) =>
                            settings?.configuration?.distributionChannelDefinition?.find(
                              (channel) => channel.id === chId
                            )?.label ?? chId
                        )
                        .join(", ")
                    : "n/a";

                const criticalityLabel = item.basicItem?.criticality;
                const criticalityKey = Object.entries(APP_CRITICALITIES).find(
                  ([, label]) => label === criticalityLabel
                )?.[0] as keyof typeof APP_CRITICALITIES | undefined;
                const criticalityColor = criticalityKey
                  ? CRITICALITY_COLORS_BY_KEY[criticalityKey]
                  : RECORD_TYPE_BADGE_COLORS.fallback;
                const recordTypeBadge = getRecordTypeBadgeConfig(
                  item.basicItem?.recordType
                );

                const itemLock =
                  lockDetails?.itemLocks?.[`item-${item.basicItem?.id}`];
                const isLocked =
                  itemLock?.isLocked &&
                  itemLock.lockedBy !== userProfile?.email;

                const openAssessmentDialog = async () => {
                  const reportingItem = item;
                  const basicItem = reportingItem?.basicItem;

                  if (!basicItem) {
                    return;
                  }

                  try {
                    await resourceLockPlugin?.lock(`item-${basicItem.id}`);
                  } catch {
                    // Item is already locked by someone else; don't navigate into the edit dialog.
                    return;
                  }

                  this.app?.patchAppState({
                    editForm: {
                      currentTrackingItem: {
                        ...basicItem,
                        distributionChannel: reportingItem?.distributionChannel,
                      },
                    },
                  });
                  this.app?.navigate(
                    generatePath(APP_ROUTES.trackingItemDialog, {
                      id: item.project?.id ?? "",
                      "tracking-item-id": basicItem?.id ?? "",
                      viewname:
                        getLocalizedMessage(language, "dashboard") ||
                        "dashboard",
                    })
                  );
                };

                const tooltipCell = {
                  autoCutBody: true,
                  component: TextCardCell,
                  cardProps: {
                    sx: {
                      zIndex: 3,
                      backgroundColor: "background.paper",
                    },
                  },

                  tooltipProps: {
                    placement: "right",
                    slotProps: {
                      tooltip: {
                        sx: {
                          backgroundColor: "#fff",
                          color: SECONDARY_TEXT_COLOR,
                          border: "1px solid",
                          borderColor: "background.paper",
                          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                          fontSize: "14px",
                          fontWeight: 400,
                          padding: "12px 16px",
                          borderRadius: "8px",
                        },
                      },
                    },
                  },
                };

                return {
                  columnProps: [
                    {
                      body: item.basicItem?.customId?.toString() ?? "",
                      cardProps: {
                        sx: {
                          position: "sticky" as const,
                          left: 0,
                          zIndex: 3,
                          backgroundColor: "background.paper",
                        },
                      },
                      bodyProps: {
                        sx: {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      },
                    },
                    {
                      body: item.basicItem?.name ?? "",
                      autoCutBody: true,
                    },
                    {
                      component: BadgeCell,
                      body: recordTypeBadge.label,
                      color: recordTypeBadge.backgroundColor,
                      textColor: getContrastColor(
                        recordTypeBadge.backgroundColor
                      ),
                    },
                    {
                      body: item.basicItem?.owner ?? "n/a",
                    },
                    {
                      ...tooltipCell,
                      component: TextCardCell,
                      body: distributionChannelLabel,
                    },
                    {
                      component: BadgeCell,
                      body: item.basicItem?.lifeCycle ?? "n/a",
                      color: RECORD_TYPE_BADGE_COLORS.fallback,
                      textColor: getContrastColor(
                        RECORD_TYPE_BADGE_COLORS.fallback
                      ),
                    },
                    {
                      component: BadgeCell,
                      body: criticalityLabel ?? "n/a",
                      color: criticalityColor,
                      textColor: CRITICALITY_TEXT_COLOR,
                    },
                    {
                      ...tooltipCell,
                      body:
                        item.basicItem?.processSteps
                          ?.filter(Boolean)
                          .join(", ") || "n/a",
                    },
                    {
                      ...tooltipCell,
                      body:
                        item.basicItem?.applicationCapabilities
                          ?.filter(Boolean)
                          .join(", ") || "n/a",
                    },
                    {
                      ...tooltipCell,
                      body:
                        item.basicItem?.technicalExposure
                          ?.filter(Boolean)
                          .join(", ") || "n/a",
                    },
                    (() => {
                      const dcRelevantOe = getDcRelevantOeDisplay(item);

                      return {
                        component: DcRelevanceCell,
                        body: dcRelevantOe,
                      };
                    })(),
                    {
                      component: BadgeCell,
                      body: item.degreeOfCompletion ?? "",
                      ...getReviewStatusBadgeProps(
                        item.degreeOfCompletion,
                        settings?.configuration
                      ),
                      cardProps: {
                        sx: {
                          position: "sticky" as const,
                          right: hasVisibleOpenColumn
                            ? `${IT_SYSTEMS_OPEN_COLUMN_WIDTH_PX}px`
                            : 0,
                          zIndex: 4,
                          minWidth: degreeColumnWidthPx,
                          width: degreeColumnWidthPx,
                          backgroundColor: "background.paper",
                        },
                      },
                    },
                    {
                      component: OpenAssessmentCell,
                      onClick: openAssessmentDialog,
                      isLocked: isLocked ?? false,
                      tooltip: getLockedByMessage("item", language, itemLock),
                      cardProps: {
                        sx: {
                          position: "sticky" as const,
                          right: 0,
                          zIndex: 4,
                          minWidth: IT_SYSTEMS_OPEN_COLUMN_WIDTH_PX,
                          width: IT_SYSTEMS_OPEN_COLUMN_WIDTH_PX,
                          backgroundColor: "background.paper",
                        },
                      },
                    },
                  ],
                  rowProps: {
                    cardProps: { sx: { cursor: "pointer" } },
                    onClick: () => {
                      if (!item.project?.id) {
                        return;
                      }

                      this.app?.navigate(
                        generatePath(APP_ROUTES.report, {
                          id: item.project.id,
                          viewname: "dashboard",
                        })
                      );
                    },
                  },
                };
              }),
              tableHeaderEntries: tableHeaderEntriesWithVisibility,
              language,
            };
          }
        );

        itSystemsOverviewTableComponent.on("reorderColumns", () => {
          if (!this.app) {
            return;
          }

          markTableConfigurationDirty(
            this.app,
            "itSystemsOverviewTableConfiguration"
          );
        });

        itSystemsOverviewTableComponent.on("addColumn", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          markTableConfigurationDirtyFromColumnPayload(
            this.app,
            "itSystemsOverviewTableConfiguration",
            event.payload
          );
        });

        itSystemsOverviewTableComponent.on("deleteColumn", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          markTableConfigurationDirtyFromColumnPayload(
            this.app,
            "itSystemsOverviewTableConfiguration",
            event.payload
          );
        });
      }
    );
  }

  /**
   * Adds every static overview card, in the exact order the ~700-line
   * monolith did (#2222): see `overview/index.ts` for the ordered list.
   */
  buildOverview() {
    if (!this.app || !this.view) {
      return;
    }

    for (const addCard of OVERVIEW_CARDS) {
      addCard(this.view, this.app);
    }
  }
}
